// ===== PreviewBox Component =====
import React, { useState, useEffect, useRef } from 'react';
import SimpleInputField from '../components/variable_Simple';
import CalculatorField from '../components/variables_Calculator';
import RichTextInput from '../components/variables_RichTest';
import { TextIcon, Calculator, Type, FileText } from 'lucide-react';
import { VariableProvider, useVariables } from '../contexts/VariableContext';
import { generateUniqueKey } from '../utils/keyGenerator';
import { resolveVariables } from '../utils/variableResolver';
import {config} from '../config';

const PreviewBox = ({ value, fieldType = "note" }) => {
  const { variableDict } = useVariables();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const typeColors = config.fieldTypes[fieldType] || config.fieldTypes.note;
  
  useEffect(() => {
    if (value) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [value]);
  
  if (!shouldRender) return null;
  
  let displayValue = '';
  let icon = null;

  if (fieldType === "note") {
    displayValue = '';
    icon = <TextIcon />;
  }
  
  if (fieldType === "variable") {
    const resolvedValue = resolveVariables(value, variableDict);
    const stringValue = String(resolvedValue);
    displayValue = stringValue.length > 10 ? stringValue.slice(0, 10) + ' ...' : stringValue;
  }
  
  if (fieldType === "calculation") {
    try {
      const resolvedValue = resolveVariables(value, variableDict);
      const result = eval(resolvedValue);
      const stringResult = String(result);
      displayValue = stringResult.length > 10 ? stringResult.slice(0, 10) + ' ...' : stringResult;
    } catch (error) {
      displayValue = "Error";
    }
  }
  
  return (
    <div 
      className={`flex flex-col justify-center absolute top-0 left-full h-14 min-w-14 ml-3 rounded-lg px-4 py-2 shadow-lg whitespace-nowrap transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        zIndex: 9999,
        backgroundColor: config.colors.fieldBg,
        border: `5px solid ${typeColors.border}`,
        color: config.colors.textPrimary
      }}
    >
      {icon}
      <div className="font-medium">{displayValue}</div>
    </div>
  );
};

function FieldWrapper({ fieldType, fieldKey, onKeyChange, children, existingKeys = [] }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(fieldKey);
  const typeColors = config.fieldTypes[fieldType] || config.fieldTypes.note;

  // Check if the current edit value is taken by another variable
  const isKeyTaken = editValue.trim() !== fieldKey && existingKeys.includes(editValue.trim());

  const getIcon = () => {
    switch (fieldType) {
      case 'calculation':
        return <Calculator className="w-4 h-4" />;
      case 'note':
        return <FileText className="w-4 h-4" />;
      case 'variable':
        return <Type className="w-4 h-4" />;
      default:
        return <Type className="w-4 h-4" />;
    }
  };

  const handleKeyBlur = () => {
    setIsEditing(false);
    if (editValue.trim() && editValue !== fieldKey && !isKeyTaken) {
      onKeyChange(editValue.trim());
    } else {
      setEditValue(fieldKey);
    }
  };

  const handleKeyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleKeyBlur();
    } else if (e.key === 'Escape') {
      setEditValue(fieldKey);
      setIsEditing(false);
    }
  };

  return (
    <div 
      className="relative min-h-[30px] rounded-xl"
      style={{
        backgroundColor: typeColors.bg,
        border: `5px solid ${typeColors.border}`
      }}
    >
      {/* Header with icon and key - positioned absolutely to overlap */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between py-1 z-10">
        {/* Icon on the left */}
        <div 
          className="flex items-center rounded px-1"
          style={{ color: typeColors.icon }}
        >
          {getIcon()}
        </div>
        
        {/* Editable key on the right */}
        <div className="flex items-center">
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleKeyBlur}
              onKeyDown={handleKeyKeyDown}
              className="text-xs font-mono px-1 py-0.5 border rounded focus:outline-none focus:ring-1 w-24"
              style={{
                backgroundColor: config.colors.fieldBg,
                borderColor: isKeyTaken ? '#ef4444' : config.colors.fieldBorder,
                color: isKeyTaken ? '#dc2626' : config.colors.textPrimary
              }}
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs font-mono px-1 py-0.5 rounded transition-colors"
              style={{
                color: config.colors.textSecondary,
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = config.colors.textPrimary;
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = config.colors.textSecondary;
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
              }}
            >
              {fieldKey}
            </button>
          )}
        </div>
      </div>
      
      {/* Field content */}
      <div className="">
        {children}
      </div>
    </div>
  );
}


function LayoutContent() {
  const { variableDict, initializeVariable, renameVariable, updateTrigger } = useVariables();
  const [fields, setFields] = useState([
    { id: 'field1', type: 'note' },
    { id: 'field2', type: 'variable' },
    { id: 'field3', type: 'calculation' },
    { id: 'field4', type: 'variable' }
  ]);

  // Function to generate or get existing key for a field
  const getOrCreateKey = (fieldId) => {
    const existingKey = Object.keys(variableDict).find(
      key => variableDict[key]?.fieldId === fieldId
    );
    
    if (existingKey) {
      return existingKey;
    }
    
    const newKey = generateUniqueKey('var', variableDict);
    initializeVariable(newKey, '', fieldId);
    
    return newKey;
  };

  // Handle key rename
  const handleKeyChange = (oldKey, newKey) => {
    if (oldKey !== newKey && !variableDict[newKey]) {
      renameVariable(oldKey, newKey);
    }
  };

  // Add new field
  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type: type
    };
    setFields(prev => [...prev, newField]);
  };

  return (
    <div 
      className="flex items-center justify-center w-full h-full" // Use w-full h-full instead
      style={{ backgroundColor: config.colors.canvas }}
    >
      <div className="relative w-[400px] h-[700px] flex flex-col">
        <div 
          className="flex-1 border p-2"
          style={{
            backgroundColor: config.colors.canvas,
            borderColor: config.colors.fieldBorder
          }}
        >
          
          {fields.map((field, index) => {
            const generatedKey = getOrCreateKey(field.id);
            
            return (
              <FieldWrapper
                key={field.id}
                fieldType={field.type}
                fieldKey={generatedKey}
                onKeyChange={(newKey) => handleKeyChange(generatedKey, newKey)}
              >
                {field.type === 'calculation' ? (
                  <CalculatorField 
                    generatedKey={generatedKey}
                    placeholder="Enter formula..."
                    className='justify-left'
                  />
                ) : field.type === 'note' ? (
                  <RichTextInput 
                    generatedKey={generatedKey}
                    placeholder="Start typing... Use @ for variables"
                    className='justify-left'
                  />
                ) : (
                  <SimpleInputField 
                    generatedKey={generatedKey}
                    className='justify-left'
                    updateTrigger={updateTrigger}
                  />
                )}
                <PreviewBox 
                  value={variableDict[generatedKey]?.value}
                  fieldType={field.type}
                />
              </FieldWrapper>
            );
          })}
          
        </div>
        
        {/* Add Field Buttons */}
        <div className="flex gap-2 justify-center">
          <div className='flex p-2 m-1 rounded-lg gap-2' style={{ backgroundColor: config.colors.textPrimary }}>
            <button
              onClick={() => addField('note')}
              className="flex items-center gap-1 px-2 py-2 text-white rounded-lg transition-colors shadow-md"
              style={{ backgroundColor: config.fieldTypes.note.button }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.note.buttonHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.note.button}
              title="Add Note Field"
            >
              <FileText className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => addField('variable')}
              className="flex items-center gap-1 px-2 py-2 text-white rounded-lg transition-colors shadow-md"
              style={{ backgroundColor: config.fieldTypes.variable.button }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.variable.buttonHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.variable.button}
              title="Add Variable Field"
            >
              <Type className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => addField('calculation')}
              className="flex items-center gap-1 px-2 py-2 text-white rounded-lg transition-colors shadow-md"
              style={{ backgroundColor: config.fieldTypes.calculation.button }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.calculation.buttonHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = config.fieldTypes.calculation.button}
              title="Add Calculation Field"
            >
              <Calculator className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export { LayoutContent }; // Add this export

export default function LayoutComponent() {
  return (
    <VariableProvider>
      <LayoutContent />
    </VariableProvider>
  );
}